
const MB = 1024 * 1024;

export const compressImage = async (file, options = {}) => {
  const { maxWidth = 1920, maxHeight = 1920, quality = 0.82, minSize = 500 * 1024 } = options;

  if (!(file instanceof File)) throw new Error("Invalid image file.");
  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) return file;
  if (file.size < minSize) return file;

  let bitmap;

  try {
    bitmap = await createImageBitmap(file);

    const scale = Math.min(1, maxWidth / bitmap.width, maxHeight / bitmap.height);
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) return file;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, width, height);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/webp", quality));
    if (!blob || blob.type !== "image/webp" || blob.size >= file.size) return file;

    const name = file.name.replace(/\.[^.]+$/, "") + ".webp";
    return new File([blob], name, { type: "image/webp", lastModified: Date.now() });
  } catch (error) {
    console.warn("Image compression skipped:", file.name, error);
    return file;
  } finally {
    bitmap?.close();
  }
};

export const compressImages = async (files, options = {}) => {
  const results = [];
  let originalBytes = 0;
  let compressedBytes = 0;

  for (const file of files) {
    const compressed = await compressImage(file, options);
    originalBytes += file.size;
    compressedBytes += compressed.size;
    results.push(compressed);
  }

  return {
    files: results,
    originalBytes,
    compressedBytes,
    savedPercent: originalBytes ? Math.round((1 - compressedBytes / originalBytes) * 100) : 0,
    originalMB: +(originalBytes / MB).toFixed(2),
    compressedMB: +(compressedBytes / MB).toFixed(2),
  };
};
